import { ButtonERP, PrimeReactModal } from "@finanedi.es/storybook"
import { DepartamentoReadingFiltersDtoProps } from "api/DTOs/DepartamentoReadingFiltersDto"
import { DepartamentoUsuarioReadingDtoProps } from "api/DTOs/DepartamentoUsuarioReadingDto"
import { getDepartamentoByFilters } from "api/Departamento.service"
import { DepartamentoReadingFiltersDtoIPagedEnumerableProps } from "api/Schemas/DepartamentoReadingFiltersDtoIPagedEnumerable"
import { DEPARTAMENTO_ROL } from "api/Types/DepartamentoRol"
import { usuarioUpdate } from "api/Usuario.service"
import FormikInputText from "common/components/FormikFields/FormikInputText"
import notification from "common/utils/notification"
import { MODAL_STYLES } from "constants/primereactStyles"
import { Formik } from "formik"
import { Dropdown, DropdownChangeParams } from "primereact/dropdown"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import * as Yup from "yup"
import { UsuarioReadingFiltersAdaptedProps } from "./TableUsuarios/UsuariosTable.adapter"
import { DepartamentoItem } from "./usersModal"

interface DepartamentoCombinedProps
  extends DepartamentoUsuarioReadingDtoProps,
    DepartamentoReadingFiltersDtoProps {}
export interface ModalDepartamentosProps {
  isOpen: boolean
  close?: () => void
  refresh: () => void
  rowData?: UsuarioReadingFiltersAdaptedProps
}
const ModalDepartamentos = ({
  isOpen,
  refresh,
  rowData,
  close,
}: ModalDepartamentosProps) => {
  const { t } = useTranslation()
  const [departamentosDisponibles, setDepartamentosDisponibles] =
    useState<DepartamentoReadingFiltersDtoIPagedEnumerableProps>()
  const [departamentosSeleccionados, setDepartamentosSeleccionados] = useState<
    Array<DepartamentoCombinedProps> | undefined | null
  >([])

  useEffect(() => {
    setDepartamentosSeleccionados(rowData?.["DepartamentoUsuarios.Count"])
    setDepartamentosDisponibles((prevState) => {
      return {
        ...(prevState ? { ...prevState } : []),
        items: prevState?.items?.filter(
          (dep) =>
            !departamentosSeleccionados?.some(
              (depSel) => depSel?.departamentoId === dep?.id
            )
        ),
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const cargarDepartamentos = async () => {
    try {
      if (isOpen) {
        const departamentos = await getDepartamentoByFilters({
          SkipCount: 0,
          MaxResultCount: 100,
        })
        setDepartamentosDisponibles((prevState) => {
          const departamentosOrdenados = departamentos?.items?.sort(
            (a, b) =>
              (a?.nombre && b?.nombre && a.nombre.localeCompare(b.nombre)) || 0
          )
          return {
            ...(prevState ? { ...prevState } : []),
            items: departamentosOrdenados?.filter((dep) => {
              return !rowData?.["DepartamentoUsuarios.Count"]?.some(
                (depSel) => {
                  return depSel?.departamentoId === dep?.id
                }
              )
            }),
          }
        })
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
    }
  }

  useEffect(() => {
    isOpen && cargarDepartamentos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  return (
    <Formik
      enableReinitialize
      initialValues={{
        nombre: rowData?.["Persona.Nombre"],
        apellidos: rowData?.["Persona.Apellidos"],
        email: rowData?.email,
        departamentos:
          rowData?.["DepartamentoUsuarios.Count"]?.map((departamento) => ({
            id: rowData?.id ?? "",
            departamentoId: departamento?.departamentoId,
            nombre: departamento?.nombre,
            esResponsable: departamento?.esResponsable,
          })) ?? [],
      }}
      validationSchema={Yup.object().shape({
        nombre: Yup.string()
          .trim()
          .required(`${t("errors.invalidName")}`),
        apellidos: Yup.string()
          .trim()
          .required(`${t("errors.invalidSubName")}`),
        email: Yup.string()
          .email()
          .required(`${t("errors.invalidMail")}`),
      })}
      validateOnBlur={false}
      onSubmit={async (values, { resetForm }) => {
        const departments = values.departamentos?.reduce(
          (acc, { id, departamentoId, esResponsable }) =>
            Object.assign(acc, {
              [departamentoId ?? id]: esResponsable
                ? DEPARTAMENTO_ROL.RESPONSABLE
                : DEPARTAMENTO_ROL.TRABAJADOR,
            }),
          {}
        )
        const requestBody = {
          ...values,
          departamentos: departments,
        }

        try {
          rowData?.id &&
            (await usuarioUpdate(rowData.id, requestBody).then(() => {
              notification.success({
                body: `${t("notification.userSuceessUpdate")}`,
              })
            }))
          close?.()
          refresh()
          resetForm()
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(err)
        }
      }}
    >
      {({ handleSubmit, handleReset, setFieldValue }) => (
        <PrimeReactModal
          visible={isOpen}
          closable={false}
          ptContext={MODAL_STYLES}
          className='tw-w-192 tw-rounded-lg tw-py-5 tw-px-6 tw-bg-white'
          baseZIndex={20}
          keepInViewport={false}
          header={
            <div className='tw-text-2xl tw-font-medium tw-text-primary tw-mb-4'>
              {`${t("gestionUsuarios.users.editModal.titleEdit")}`}
            </div>
          }
        >
          <form
            className='tw-w-full tw-flex tw-flex-col tw-text-base tw-font-normal tw-gap-4 tw-text-text'
            name='usuarios-form'
          >
            <div className='tw-flex tw-flex-col tw-w-full'>
              <label htmlFor='nombre'>
                {t("gestionUsuarios.users.editModal.labelName")}
              </label>
              <FormikInputText
                id='nombre'
                name='nombre'
              />
            </div>
            <div className='tw-flex tw-flex-col tw-w-full'>
              <label htmlFor='apellidos'>
                {t("gestionUsuarios.users.editModal.labelSubName")}
              </label>
              <FormikInputText
                id='apellidos'
                name='apellidos'
              />
            </div>
            <div className='tw-flex tw-flex-col tw-w-full'>
              <label htmlFor='email'>
                {t("gestionUsuarios.users.editModal.labelMail")}
              </label>
              <FormikInputText
                disabled
                id='email'
                name='email'
              />
            </div>
            <div className='tw-flex tw-flex-col tw-w-full'>
              <label htmlFor='departamento'>
                {t("gestionUsuarios.users.editModal.labelDepartament")}
              </label>
              <Dropdown
                filter
                options={departamentosDisponibles?.items ?? []}
                placeholder={`${t(
                  "gestionUsuarios.users.editModal.placeholderDepartament"
                )}`}
                emptyFilterMessage='Sin resultados...'
                emptyMessage={`${t(
                  "gestionUsuarios.users.editModal.emptyMsgDepartament"
                )}`}
                id='departamento'
                name='departamento'
                onChange={(e: DropdownChangeParams) => {
                  setDepartamentosDisponibles((prev) => ({
                    ...(prev ? { ...prev } : []),
                    items: prev?.items?.filter((dep) => dep.id !== e.value.id),
                  }))
                  const departamentoSeleccionado =
                    departamentosDisponibles?.items?.find(
                      (dep) => dep.id === e.value.id
                    )
                  if (departamentoSeleccionado) {
                    setDepartamentosSeleccionados((prev) => {
                      const selection = prev?.length
                        ? [...prev, departamentoSeleccionado]
                        : [departamentoSeleccionado]
                      setFieldValue("departamentos", selection)
                      return selection
                    })
                  }
                }}
                resetFilterOnHide
                optionLabel='nombre'
                panelClassName='!tw-z-[2302]'
              />
            </div>
            <div className='tw-bg-primary-10 tw-p-4 tw-rounded tw-h-72 tw-overflow-auto'>
              {Array.isArray(departamentosSeleccionados) &&
                departamentosSeleccionados?.map((departamento) => (
                  <DepartamentoItem
                    key={departamento?.departamentoId ?? departamento.id}
                    deleteDepartamentoItem={(id: string) => {
                      setDepartamentosSeleccionados((prev) => {
                        const selection = prev?.filter((dep) =>
                          dep?.departamentoId
                            ? dep?.departamentoId !== id
                            : dep?.id !== id
                        )
                        setFieldValue("departamentos", selection)
                        return selection
                      })
                      const departamentoAQuitar =
                        departamentosSeleccionados.find((dep) =>
                          dep?.departamentoId
                            ? dep?.departamentoId === id
                            : dep?.id === id
                        )
                      if (departamentoAQuitar) {
                        setDepartamentosDisponibles((prev) => ({
                          ...(prev ? { ...prev } : []),
                          items:
                            prev?.items &&
                            [
                              ...prev.items,
                              {
                                ...departamentoAQuitar,
                                esResponsable: false,
                              },
                            ].sort(
                              (a, b) =>
                                (a?.nombre &&
                                  b?.nombre &&
                                  a.nombre.localeCompare(b.nombre)) ||
                                0
                            ),
                        }))
                      }
                    }}
                    handleStatusResponsable={(id: string, status: boolean) => {
                      const departamentoResponsable =
                        departamentosSeleccionados.find(
                          (dep) => dep.id === id || dep.departamentoId === id
                        )
                      if (departamentoResponsable) {
                        setDepartamentosSeleccionados((prev) => {
                          const selection = prev?.map((dep) =>
                            dep.id === id || dep.departamentoId === id
                              ? {
                                  ...dep,
                                  esResponsable: status,
                                }
                              : dep
                          )
                          setFieldValue("departamentos", selection)
                          return selection
                        })
                      }
                    }}
                    entry={departamento}
                  />
                ))}
            </div>
          </form>
          <div
            key='actions'
            className='tw-pb-1 tw-flex tw-flex-col sm:tw-flex-row tw-justify-end tw-pt-4 tw-gap-4'
          >
            <ButtonERP
              customize={{
                type: "transparent",
                size: "md",
              }}
              label={`${t("gestionUsuarios.users.editModal.buttonCancel")}`}
              onClick={(e) => {
                handleReset(e)
                setDepartamentosSeleccionados([])
                close?.()
              }}
            />
            <ButtonERP
              customize={{
                type: "primary",
                size: "md",
              }}
              onClick={() => handleSubmit()}
              label={`${t("gestionUsuarios.users.editModal.buttonSave")}`}
            />
          </div>
        </PrimeReactModal>
      )}
    </Formik>
  )
}

export default ModalDepartamentos
